import React, { useState } from 'react';
import { SectorInterventionList } from './SectorLens';
import { labelsForMode, MODES } from '../lib/opportunityMode';
import {
  ANALYSIS_TABS,
  DEFAULT_ANALYSIS_TAB,
  hasMarketplaceFixTab,
  getMarketplaceFixContent,
  readinessPillLabel,
  formatMarketplaceRecommendation,
} from '../lib/analysisTabs';

function getPriorityColor(priority) {
  switch (priority) {
    case 'critical': return 'var(--danger)';
    case 'high': return '#DC2626';
    case 'medium': return 'var(--warning)';
    case 'low': return 'var(--success)';
    default: return 'var(--text-secondary)';
  }
}

function getPriorityBadge(priority) {
  return (
    <span className="badge" style={{
      background: priority === 'P0' ? '#FEE2E2' : priority === 'P1' ? '#FEF3C7' : '#DCFCE7',
      color: priority === 'P0' ? 'var(--danger)' : priority === 'P1' ? 'var(--warning)' : 'var(--success)',
      borderColor: priority === 'P0' ? '#FECACA' : priority === 'P1' ? '#FDE68A' : '#BBF7D0'
    }}>
      {priority}
    </span>
  );
}

export default function FixerResults({ roadmap, onBack, onDownloadPDF, onDownloadPNG, onGenerateFit, fitLoading }) {
  // Iter 35c — Two-tab UI. Consultancy View is the protected default
  // and always renders the legacy Remediation Roadmap layout.
  // Marketplace Opportunity tab appears only when the Iter-35a
  // additive `marketplace_opportunity.fix_pack` is present and
  // contains renderable activation content.
  const [activeTab, setActiveTab] = useState(DEFAULT_ANALYSIS_TAB);
  const showMarketplaceTab = hasMarketplaceFixTab(roadmap);
  const resolvedTab = (activeTab === ANALYSIS_TABS.MARKETPLACE && showMarketplaceTab)
    ? ANALYSIS_TABS.MARKETPLACE
    : ANALYSIS_TABS.CONSULTANCY;

  if (!roadmap) return null;

  return (
    <div>
      {showMarketplaceTab && (
        <FixerTabBar
          activeTab={resolvedTab}
          onChange={setActiveTab}
        />
      )}
      {resolvedTab === ANALYSIS_TABS.CONSULTANCY ? (
        <ConsultancyFixView
          roadmap={roadmap}
          onBack={onBack}
          onDownloadPDF={onDownloadPDF}
          onDownloadPNG={onDownloadPNG}
          onGenerateFit={onGenerateFit}
          fitLoading={fitLoading}
        />
      ) : (
        <MarketplaceFixView
          roadmap={roadmap}
          onBack={onBack}
          onDownloadPDF={onDownloadPDF}
          onDownloadPNG={onDownloadPNG}
          onGenerateFit={onGenerateFit}
          fitLoading={fitLoading}
        />
      )}
    </div>
  );
}


// ── Tab bar ──────────────────────────────────────────────────────────

function FixerTabBar({ activeTab, onChange }) {
  return (
    <div
      className="panel"
      style={{ marginBottom: '8px', padding: 0 }}
      data-testid="fixer-tabs"
    >
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <TabButton
          label="Consultancy View"
          active={activeTab === ANALYSIS_TABS.CONSULTANCY}
          onClick={() => onChange(ANALYSIS_TABS.CONSULTANCY)}
          testId="fixer-tab-consultancy"
        />
        <TabButton
          label="Marketplace Opportunity"
          active={activeTab === ANALYSIS_TABS.MARKETPLACE}
          onClick={() => onChange(ANALYSIS_TABS.MARKETPLACE)}
          testId="fixer-tab-marketplace"
        />
      </div>
    </div>
  );
}

function TabButton({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-selected={active}
      role="tab"
      style={{
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
        color: active ? 'var(--primary)' : 'var(--text-secondary)',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: active ? 600 : 500,
        letterSpacing: '0.02em',
        padding: '10px 16px',
        marginBottom: '-1px',
      }}
    >
      {label}
    </button>
  );
}


// ── Consultancy View (legacy Remediation Roadmap, restored as default) ──

function ConsultancyFixView({ roadmap, onBack, onDownloadPDF, onDownloadPNG, onGenerateFit, fitLoading }) {
  const workstreams = roadmap.workstreams || [];
  const quickWins = roadmap.quick_wins || [];
  const sectorInterventions = roadmap?.sector_lens?.interventions || [];

  // Iter 35c — Consultancy View *always* uses consultancy labels and
  // the legacy roadmap layout regardless of the persisted
  // `opportunity_mode`. The Iter 34 mode-aware Opportunity Plan
  // panel, mode pill and CTA override are no longer the default —
  // they move to the Marketplace tab.
  const labels = labelsForMode(MODES.CONSULTANCY);
  const headingTitle = roadmap.roadmap_title || labels.fixTitle;

  return (
    <div data-testid="fixer-consultancy-view">
      {roadmap._fallback_mode && (
        <div className="panel" style={{ marginBottom: '8px', borderLeft: '3px solid var(--warning)' }}>
          <div className="panel-body" style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-secondary)' }} data-testid="fallback-mode-banner">
            This roadmap was generated using rule-based analysis (LLM unavailable — check Universal Key balance). Regenerate for AI-powered, evidence-cited recommendations.
          </div>
        </div>
      )}

      <div className="panel" style={{ marginBottom: '12px' }}>
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="roadmap-title">
              {headingTitle}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {roadmap.scope_label || roadmap.company_name} &middot; Priority:{' '}
              <span style={{ color: getPriorityColor(roadmap.priority_level), fontWeight: '600', textTransform: 'uppercase' }} data-testid="roadmap-priority">
                {roadmap.priority_level}
              </span>
              {roadmap.estimated_timeline_months && (
                <span> &middot; Est. {roadmap.estimated_timeline_months} months</span>
              )}
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={onBack} data-testid="back-to-analysis-button">
            Back to Analysis
          </button>
        </div>
      </div>

      {quickWins.length > 0 && (
        <div className="panel" style={{ marginBottom: '12px' }}>
          <div className="panel-header">Quick Wins</div>
          <div className="panel-body" style={{ padding: '8px' }}>
            {quickWins.map((qw, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                padding: '8px 12px', borderBottom: i < quickWins.length - 1 ? '1px solid #E2E8F0' : 'none'
              }} data-testid={`quick-win-${i}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{qw.action}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {qw.expected_impact}
                  </div>
                </div>
                <span className="badge badge-type" style={{ marginLeft: '12px', whiteSpace: 'nowrap' }}>
                  {qw.timeline}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <SectorInterventionList interventions={sectorInterventions} initialVisible={3} testIdPrefix="fix-sector-interventions" />

      <div className="panel" style={{ marginBottom: '12px' }}>
        <div className="panel-header">
          Workstreams
          <span className="badge badge-type" data-testid="workstream-count">{workstreams.length}</span>
        </div>
        <div className="panel-body" style={{ padding: '8px' }}>
          {workstreams.map((ws, i) => (
            <div key={i} style={{
              border: '1px solid var(--border)', marginBottom: '8px', background: 'var(--bg)'
            }} data-testid={`workstream-${i}`}>
              <div style={{
                padding: '10px 14px', borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'var(--surface)'
              }}>
                <div>
                  <span style={{ fontSize: '13px', fontWeight: '700' }}>{ws.name}</span>
                  <span style={{ marginLeft: '8px' }}>{getPriorityBadge(ws.priority)}</span>
                </div>
              </div>
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: '1.5' }}>
                  {ws.objective}
                </div>

                {ws.evidence && ws.evidence.length > 0 && (
                  <div style={{ marginBottom: '8px', padding: '6px 10px', background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: '3px solid var(--primary)' }}
                    data-testid={`workstream-evidence-${i}`}>
                    <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Supporting Evidence
                    </div>
                    {ws.evidence.map((ev, k) => (
                      <div key={k} style={{ fontSize: '11px', color: 'var(--text)', padding: '2px 0', lineHeight: '1.4' }}>
                        &bull; {ev}
                      </div>
                    ))}
                  </div>
                )}

                <table className="data-table" style={{ marginBottom: '8px' }}>
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Timeline</th>
                      <th>Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ws.actions || []).map((action, j) => (
                      <tr key={j}>
                        <td style={{ fontSize: '12px', fontWeight: '500' }}>{action.action}</td>
                        <td><span className="badge badge-type">{action.timeline}</span></td>
                        <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{action.impact}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ display: 'flex', gap: '16px', fontSize: '11px' }}>
                  {ws.success_metrics && ws.success_metrics.length > 0 && (
                    <div>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Metrics: </span>
                      <span style={{ color: 'var(--success)' }}>{ws.success_metrics.join(', ')}</span>
                    </div>
                  )}
                  {ws.risks && ws.risks.length > 0 && (
                    <div>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Risks: </span>
                      <span style={{ color: 'var(--danger)' }}>{ws.risks.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {roadmap.executive_recommendation && (
        <div className="panel" style={{ marginBottom: '12px' }}>
          <div className="panel-header">Executive Recommendation</div>
          <div className="panel-body">
            <div className="executive-summary" data-testid="executive-recommendation">
              {roadmap.executive_recommendation}
            </div>
          </div>
        </div>
      )}

      <FixerActionBar
        onGenerateFit={onGenerateFit}
        fitLoading={fitLoading}
        ctaLabel={labels.generateFitCta}
        onDownloadPDF={onDownloadPDF}
        onDownloadPNG={onDownloadPNG}
        onBack={onBack}
      />
    </div>
  );
}


// ── Marketplace Opportunity View (sourced from marketplace_opportunity.fix_pack) ──

function MarketplaceFixView({ roadmap, onBack, onDownloadPDF, onDownloadPNG, onGenerateFit, fitLoading }) {
  const content = getMarketplaceFixContent(roadmap);
  if (!content) return null;

  const {
    title, angle, why_this_fits, first_conversation, groups,
    offer_hook, watchouts, evidence_strength, roles, recommendation,
    composite_score, readiness_mode, sector,
    best_fit_role, best_fit_role_label, role_scores,
    role_explanation, role_watchouts, evidence_to_validate,
  } = content;

  const pill = readinessPillLabel(readiness_mode);
  const ctaLabels = labelsForMode(readiness_mode || MODES.CONSULTANCY);

  return (
    <div data-testid="fixer-marketplace-view">
      <div className="panel" style={{ marginBottom: '12px' }}>
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div
              style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
              data-testid="marketplace-plan-title"
            >
              {title || 'Marketplace Activation Plan'}
              {best_fit_role_label && (
                <span className="badge badge-type" style={{ fontSize: '10px' }} data-testid="marketplace-plan-role-chip">
                  Role: {best_fit_role_label}
                </span>
              )}
              {pill && (
                <span className="badge badge-type" style={{ fontSize: '10px' }} data-testid="marketplace-plan-mode-pill">
                  {pill}
                </span>
              )}
              {sector && (
                <span className="badge badge-type" style={{ fontSize: '10px' }} data-testid="marketplace-plan-sector-badge">
                  {sector}
                </span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {roadmap.scope_label || roadmap.company_name}
              {recommendation && (
                <span data-testid="marketplace-plan-recommendation" style={{ marginLeft: '8px' }}>
                  &middot; <strong>{formatMarketplaceRecommendation(recommendation)}</strong>
                </span>
              )}
              {typeof composite_score === 'number' && (
                <span data-testid="marketplace-plan-score" style={{ marginLeft: '8px' }}>
                  &middot; Score {composite_score} / 100
                </span>
              )}
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={onBack} data-testid="marketplace-back-to-analysis">
            Back to Analysis
          </button>
        </div>
      </div>

      {(best_fit_role || (role_explanation && role_explanation.length > 0)
        || (role_scores && Object.keys(role_scores).length > 0)) && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="marketplace-plan-role-panel">
          <div className="panel-header" title="Role classification derived from the single Marketplace role authority">
            Marketplace Role
          </div>
          <div className="panel-body" style={{ padding: '10px 14px' }}>
            {best_fit_role_label && (
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }} data-testid="marketplace-plan-role-name">
                {best_fit_role_label}
                {best_fit_role && (
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: '6px' }}>
                    ({best_fit_role})
                  </span>
                )}
              </div>
            )}
            {role_scores && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', marginBottom: '6px' }} data-testid="marketplace-plan-role-scores">
                {typeof role_scores.buyer_score === 'number' && (
                  <span data-testid="marketplace-plan-role-score-buyer"><strong>Buyer:</strong> {role_scores.buyer_score}</span>
                )}
                {typeof role_scores.supplier_partner_score === 'number' && (
                  <span data-testid="marketplace-plan-role-score-supplier-partner"><strong>Supplier / Partner:</strong> {role_scores.supplier_partner_score}</span>
                )}
                {typeof role_scores.ecosystem_score === 'number' && (
                  <span data-testid="marketplace-plan-role-score-ecosystem"><strong>Ecosystem:</strong> {role_scores.ecosystem_score}</span>
                )}
              </div>
            )}
            {role_explanation && role_explanation.length > 0 && (
              <div style={{ marginTop: '6px' }} data-testid="marketplace-plan-role-explanation">
                {role_explanation.map((e, i) => (
                  <div key={i} style={{ fontSize: '12px', color: 'var(--text)', padding: '1px 0' }} data-testid={`marketplace-plan-role-explanation-${i}`}>
                    &bull; {e}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {roles && roles.length > 0 && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="marketplace-plan-roles-panel">
          <div className="panel-header" title="Marketplace participant roles registered for this sector">
            Sector Marketplace Roles
          </div>
          <div className="panel-body" style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {roles.map((role, i) => (
              <span
                key={`${role}-${i}`}
                className="badge badge-type"
                style={{ fontSize: '11px', textTransform: 'capitalize' }}
                data-testid={`marketplace-plan-role-${i}`}
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="analysis-grid" style={{ marginBottom: '12px' }}>
        <div>
          <Section
            title="Recommended Marketplace Angle"
            tooltip="The best-fit operator approach for this target, derived from the strongest readiness driver"
            testId="marketplace-plan-angle"
            empty="No angle suggested yet — re-run Fix once approved signals are richer."
          >
            {angle}
          </Section>
          <Section
            title="Why This Angle Fits"
            tooltip="What the approved evidence says about why this angle is the strongest fit"
            testId="marketplace-plan-why-this-fits"
            empty="Evidence summary not yet available."
            spacingTop
          >
            {why_this_fits}
          </Section>
          <Section
            title="Marketplace Offer Hook"
            tooltip="The single most actionable opening line for outreach"
            testId="marketplace-plan-offer-hook"
            empty="Offer hook not yet computed."
            spacingTop
          >
            {offer_hook}
          </Section>
        </div>

        <div>
          <Section
            title="Suggested First Conversation"
            tooltip="The recommended opening move with the prospect"
            testId="marketplace-plan-first-conversation"
            empty="No first-move recommendation available."
          >
            {first_conversation}
          </Section>
          {evidence_to_validate && evidence_to_validate.length > 0 && (
            <div className="panel" style={{ marginTop: '4px' }} data-testid="marketplace-plan-evidence-to-validate">
              <div className="panel-header" title="Evidence the operator must validate before committing to this angle">
                Evidence to Validate
              </div>
              <div className="panel-body" style={{ padding: '10px 14px' }}>
                {evidence_to_validate.map((e, i) => (
                  <div
                    key={i}
                    style={{ fontSize: '12px', color: 'var(--text)', padding: '2px 0' }}
                    data-testid={`marketplace-plan-evidence-to-validate-${i}`}
                  >
                    &bull; {e}
                  </div>
                ))}
              </div>
            </div>
          )}
          {typeof evidence_strength === 'string' && evidence_strength && (
            <Section
              title="Evidence Strength"
              tooltip="Approved-signal corpus strength behind the score"
              testId="marketplace-plan-evidence-strength"
              spacingTop
            >
              {evidence_strength}
            </Section>
          )}
        </div>
      </div>

      <div className="panel" style={{ marginBottom: '12px' }} data-testid="marketplace-plan-activation-steps">
        <div className="panel-header" title="Concrete activation steps, grouped by approach angle">
          Activation Steps
        </div>
        <div className="panel-body" style={{ padding: '8px' }}>
          {groups.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
              No activation steps surfaced — confirm signal corpus and re-run Fix.
            </div>
          ) : (
            groups.map((g, gi) => (
              <div
                key={gi}
                style={{ padding: '8px 12px', borderTop: gi > 0 ? '1px solid var(--border)' : 'none' }}
                data-testid={`marketplace-plan-group-${gi}`}
              >
                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }} data-testid={`marketplace-plan-group-${gi}-title`}>
                  {g.title}
                </div>
                {(g.items || []).map((it, ii) => (
                  <div
                    key={ii}
                    style={{ fontSize: '12px', color: 'var(--text-secondary)', padding: '3px 0', lineHeight: '1.5' }}
                    data-testid={`marketplace-plan-group-${gi}-item-${ii}`}
                  >
                    &bull; {it.action}
                    {it.metric && (
                      <span style={{ marginLeft: '8px', color: 'var(--success)' }}>
                        ({it.metric})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {((watchouts && watchouts.length > 0)
        || (role_watchouts && role_watchouts.length > 0)) && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="marketplace-plan-watchouts">
          <div className="panel-header" title="Reasons to verify or hold before approaching the target">
            Risks / Watch-outs
          </div>
          <div className="panel-body" style={{ padding: '8px 14px' }}>
            {(watchouts || []).map((w, i) => (
              <div
                key={`w-${i}`}
                style={{ fontSize: '12px', padding: '3px 0', color: 'var(--warning)' }}
                data-testid={`marketplace-plan-watchout-${i}`}
              >
                &bull; {w}
              </div>
            ))}
            {(role_watchouts || []).map((w, i) => (
              <div
                key={`rw-${i}`}
                style={{ fontSize: '12px', padding: '3px 0', color: 'var(--warning)' }}
                data-testid={`marketplace-plan-role-watchout-${i}`}
              >
                &bull; {w}
              </div>
            ))}
          </div>
        </div>
      )}

      <FixerActionBar
        onGenerateFit={onGenerateFit}
        fitLoading={fitLoading}
        ctaLabel={ctaLabels.generateFitCta}
        onDownloadPDF={onDownloadPDF}
        onDownloadPNG={onDownloadPNG}
        onBack={onBack}
      />
    </div>
  );
}


// ── Shared bits ──────────────────────────────────────────────────────

function Section({ title, tooltip, testId, children, empty, spacingTop }) {
  return (
    <div className="panel" style={{ marginTop: spacingTop ? '4px' : 0 }} data-testid={testId}>
      <div className="panel-header" title={tooltip}>{title}</div>
      <div className="panel-body" style={{ padding: '10px 14px', fontSize: '13px', lineHeight: '1.5' }}>
        {children ? children : (
          <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>{empty}</span>
        )}
      </div>
    </div>
  );
}

function FixerActionBar({ onGenerateFit, fitLoading, ctaLabel, onDownloadPDF, onDownloadPNG, onBack }) {
  return (
    <div style={{ paddingTop: '8px', display: 'flex', gap: '8px' }}>
      {onGenerateFit && (
        <button className="btn btn-primary" onClick={onGenerateFit} disabled={fitLoading} data-testid="generate-fit-button">
          {fitLoading ? 'Generating Fit...' : ctaLabel}
        </button>
      )}
      {onDownloadPDF && (
        <button className="btn btn-outline" onClick={onDownloadPDF} data-testid="download-roadmap-pdf-button">
          Download PDF
        </button>
      )}
      {onDownloadPNG && (
        <button className="btn btn-outline" onClick={onDownloadPNG} data-testid="download-roadmap-png-button">
          Export as PNG
        </button>
      )}
      <button className="btn btn-outline" onClick={onBack} data-testid="back-button">
        Back to Analysis
      </button>
    </div>
  );
}
